import moment from 'moment';
import * as bd from './boat-data.js';
import * as geom from '../utils/geometry-util.js';
import { StationSeries } from '../weather/StationSeries.js';

/*
Example data
{
  "annotations": [],
  "course": {
    "startLine": [
      {
        "lat": -41.2575961638626,
        "lon": 174.88790290529772
      },
      {
        "lat": -41.25778722303199,
        "lon": 174.8974971471264
      ]
    },
    "startTime": "2025-06-21T22:06:59.000Z",
    "finishLine": null,
    "marks": [
      {
        "id": 1,
        "lat": -41.27775516595571,
        "lon": 174.90037792516694
      },
      {
        "id": 2,
        "lat": -41.23455418563433,
        "lon": 174.87324565117876
      },
      {
        "id": 3,
        "lat": -41.255767476040674,
        "lon": 174.87848530829802
      }
    ],
    "markList": "1P 2P 3P"
  }
}
*/

export class Info {
  static FUZZY_MARK_RADIUS_M = 60;

  constructor(data, fileName, fileId) {
    this.annotationSequence = [];
    this.fileId = fileId || null;
    this.fileName = fileName || null;
    this.setRaceInfo(data);
    this.stripLegacyRoundings();
    this.courseLegs = this.calcCourseLegs();
  }

  setFileId(id) {
    this.fileId = id;
  }

  setRaceInfo(r) {
    this.info = r != null ? r : Info.getEmptyRaceInfo();
    this.orderRaceInfoAnnotations();
  }

  static infoFileNameFor(raceFileName) {
    if (raceFileName) {
      const baseName = raceFileName.replace(/\..+$/, '');
      return `__info.${baseName}.json`;
    }
    return null;
  }

  static loadFromJSON(text, fileName, raceInfoFileId) {
    const raceInfo = bd.importRaceInfoJSON(text);
    const r = new Info(raceInfo, fileName, raceInfoFileId);
    return r;
  }

  static getEmptyRaceInfo() {
    return { annotations: [], course:{} };
  }

  serialiseToJSON() {
    if (!this.info) return null;
    return JSON.stringify(this.info, null, 2);
  }

  getCourse() {
    return this.info.course || null;
  }

  setCourse(courseData) {
    this.info.course = courseData;
    this.stripLegacyRoundings();
  }

  stripLegacyRoundings() {
    if (this.info?.course?.markList) {
      this.info.course.markList = this.info.course.markList.replace(/(\d+)[PSps]/g, '$1');
    }
  }

  clearCourse() {
    this.info.course = {};
  }

  getCourseStartTime() {
    if (this.info?.course?.startTime == null) return null;
    return moment(this.info.course.startTime);
  }

  storeWeatherStations(stations) {
    if (!this.info) 
      throw new Error("trying to set weather stations before info set");
    this.info.weatherStations = StationSeries.serialiseToObj(stations);
  }

  loadWeatherStations() {
    if (!this.info?.weatherStations) 
      return [];
    return StationSeries.fromObjects(this.info.weatherStations);
  }

  parseMarksList(markListStr) {
    const str = markListStr || this.getMarkList();
    const marks = this.getMarks();
    
    if (!str || !str.trim())
      return { error: 'Invalid mark list entry: empty mark list' };

    const result = [];
    const tokens = str.trim().split(/[\s,]+/);

    for (const token of tokens) {
      if (!token) continue;
      // Support legacy format with P/S suffix for backward compatibility
      const match = token.match(/^(\d+)([PSps])?$/);
      if (!match)
        return { error: `Invalid mark list entry: ${token}` };

      const id = parseInt(match[1], 10);

      if (!marks.some(m => m.id === id))
        return { error: `Invalid mark list entry: mark ${id} does not exist` };

      result.push({ id });
    }

    if (result.length === 0)
      return { error: 'Invalid mark list entry: no valid entries' };

    return result;
  }


  getCourseLegs() {
    return this.courseLegs ? this.courseLegs : [];
  }

  getLegById(legId) {
    const legs = this.getCourseLegs();
    if (legId == 'S') return legs[0];
    if (legId == 'F') return legs[legs.length-1];
    return legs[legId];
  }

  updateCourseLegs() {
    this.courseLegs = this.calcCourseLegs();
  }
  
  calcCourseLegs() {
    console.log("Calc course legs for:",this.fileName);
    const courseLegs = [];
    const course = this.getCourse();
    if (course == null) return null;

    const parsed = this.parseMarksList();
    if (parsed.error) return null;

    const startLine = this.getStartLine();
    if (startLine == null || startLine[0] == null || startLine[1] == null)
      return null;

    // courseLegs.push({'boundary':this.getStartLine()});
    let prevPoint = geom.lineMidpoint(this.getStartLine());
    let mark = null;
    for(let i=0; i<parsed.length; i++) {
      const markId = parsed[i].id;
      mark = course.marks.find(m => m.id === markId);
      const nextMarkData = i < parsed.length - 1 ? parsed[i+1] : null;
      let nextMark = geom.lineMidpoint(this.getFinishLine());
      if (nextMarkData)
        nextMark = course.marks.find(m => m.id === nextMarkData.id);

      const markBoundary = geom.courseLegBoundary(prevPoint, mark, nextMark, 'P');
      courseLegs.push({
        'boundary':markBoundary, 
        'start': prevPoint, 
        'end':mark, 
        'length': geom.distanceM(prevPoint, mark),
        'bearing': geom.bearingBetweenPoints(prevPoint, mark),
        'radius': Info.FUZZY_MARK_RADIUS_M
      });
      prevPoint = mark;
    }
    mark = geom.lineMidpoint(this.getFinishLine());
    courseLegs.push({
      'boundary':this.getFinishLine(), 
      'start': prevPoint, 
      'end':mark,
      'length': geom.distanceM(prevPoint, mark),
      'bearing': geom.bearingBetweenPoints(prevPoint, mark)
    });
    // console.log(JSON.stringify(courseLegs));
    return courseLegs;
  } 

  getFinishLine() {
    if (this.info.course == null) return null;
    const finishLine = this.info.course.finishLine;
    if (finishLine == null || finishLine[0] == null || finishLine[1] == null) 
      return this.info.course.startLine;
    return finishLine;
  }

  getStartLine() {
    if (this.info.course == null) return null;
    return this.info.course?.startLine;
  }

  setStartLine(point1, point2) {
    if (point1 == null && point2 == null)
      this.clearStartLine();
    else
      this.info.course.startLine = [point1, point2];
  }

  getFinishLinePoints() {
    return this.info.course.finishLine;
  }

  setFinishLine(point1, point2) {
    if (point1 == null && point2 == null)
      this.clearFinishLine();
    else
      this.info.course.finishLine = [point1, point2];
  }

  clearFinishLine() {
    this.info.course.finishLine = null;
  }

  clearStartLine() {
    this.info.course.startLine = null;
  }

  getMarks() {
    return this.info.course.marks || [];
  }

  addMark(id, lat, lon) {
    if (!this.info.course.marks) this.info.course.marks = [];
    this.info.course.marks.push({ id, lat, lon });
  }

  updateMark(id, lat, lon) {
    if (!this.info.course.marks) return;
    const mark = this.info.course.marks.find(m => m.id === id);
    if (mark) {
      mark.lat = lat;
      mark.lon = lon;
    }
  }

  deleteMark(id) {
    if (!this.info.course || !this.info.course.marks) return;
    this.info.course.marks = this.info.course.marks.filter(m => m.id !== id);
  }

  getMarkList() {
    return this.info.course.markList || '';
  }

  setMarkList(markListStr) {
    this.info.course.markList = markListStr;
  }

  getStartTime() {
    return this.info.course.startTime || null;
  }

  setStartTime(startTime) {
    this.info.course.startTime = startTime;
  }

  isFinishExplicit() {
    return this.info.course.finishLine != null;
  }

  getNextMarkId() {
    const marks = this.getMarks();
    if (marks.length === 0) return 1;
    return Math.max(...marks.map(m => m.id)) + 1;
  }

  buildDefaultMarkList() {
    const marks = this.getMarks();
    return marks.map(m => `${m.id}`).join(' ');
  }

  removeMarkFromList(markId) {
    const markList = this.getMarkList();
    // Support legacy format with optional P/S suffix
    const regex = new RegExp(`\\b${markId}([PS])?\\b`, 'gi');
    const updated = markList.replace(regex, '').replace(/\s+/g, ' ').trim();
    this.setMarkList(updated);
  }

  prettyPrintCourse() {
    const course = this.getCourse();
    if (!course) return 'No course defined';

    const parsed = this.parseMarksList();
    const legs = this.calcCourseLegs();
    const marks = this.getMarks();

    const findMarkId = (point) => {
      const m = marks.find(m => m.lat === point.lat && m.lon === point.lon);
      return m ? `Mark ${m.id}` : 'Start/Finish';
    };

    const lines = [];
    lines.push(`Course: ${course.markList}`);
    lines.push(`Start time: ${course.startTime || 'not set'}`);
    lines.push('');

    if (!parsed.error) {
      lines.push('Marks:');
      parsed.forEach((m, i) => lines.push(`  ${i + 1}. Mark ${m.id}`));
      lines.push('');
    }

    if (legs) {
      lines.push('Legs:');
      legs.forEach((leg, i) => {
        const from = findMarkId(leg.start);
        const to = findMarkId(leg.end);
        lines.push(`  Leg ${i + 1}: ${from} → ${to}`);
        const boundaryBearing = leg.boundary ? geom.bearingBetweenPoints(...leg.boundary).toFixed(1) : 'n/a';
        lines.push(`    Bearing: ${leg.bearing.toFixed(1)}°  Length: ${(leg.length / 1852).toFixed(2)} nm  Boundary: ${boundaryBearing}°`);
      });
    }

    return lines.join('\n');
  }

  validateCourse() {
    const errors = [];
    const course = this.getCourse();
    if (!course) {
      errors.push('No course defined');
      return errors;
    }
    if (!course.startTime) errors.push('Race start time not set');
    if (!course.startLine || course.startLine.length !== 2) errors.push('Start line not complete');
    if (!course.marks || course.marks.length === 0) errors.push('At least one mark required');
    return errors;
  }

  getAnnotations() {
    return this.info.annotations;
  }

  getAnnotationSequence() {
    return this.annotationSequence;
  }

  pushAnnotation(a) {
    this.info.annotations.push(a);
    this.orderRaceInfoAnnotations();
  }

  spliceAnnotations(idx, delCount) {
    this.info.annotations.splice(idx, delCount);
    this.orderRaceInfoAnnotations();
  }

  orderRaceInfoAnnotations() {
    this.annotationSequence = Info.createAnnotationSequence(this.info.annotations);
  }

  static createAnnotationSequence(annotations) {
    annotations.sort((a, b) => a.begin_ts.diff(b.begin_ts, 'seconds') + a.end_ts.diff(b.end_ts, 'years'));
    let sequence = [];
    if (annotations.length > 0) {
      const anns = annotations;
      let next = anns[0].begin_ts;
      let last = anns.reduce((max, next) => max.end_ts.isAfter(next.end_ts) ? max : next).end_ts;
      while (next.isBefore(last)) {
        const begin = next;
        const parts = anns.filter(x => begin.isSameOrAfter(x.begin_ts) && begin.isBefore(x.end_ts));
        const a = Info.mergeAnnotations(parts);
        const nextStart = Info.smallestGreaterThanZero(anns, x => x.begin_ts.diff(begin));
        const nextEnd = Info.smallestGreaterThanZero(anns, x => x.end_ts.diff(begin));
        if (nextStart == null || nextStart == undefined) {
          next = nextEnd.end_ts;
        } else {
          next = nextStart.begin_ts.isSameOrBefore(nextEnd.end_ts) ? nextStart.begin_ts : nextEnd.end_ts;
        }
        if (a != null) {
          a.begin_ts = begin;
          a.end_ts = next
          sequence.push(a);
        }
      }
    }
    sequence = sequence.filter(x => x.end_ts.diff(x.begin_ts, "minutes") > 4);
    return sequence;
  }

  getAnnotationFor(ts) {
    ts = moment(ts);
    return this.annotationSequence.find(x => ts.isSameOrAfter(x.begin_ts) && ts.isBefore(x.end_ts) && x.end_ts.diff(x.begin_ts, "minutes") > 4);
  }

  static mergeAnnotations(anns) {
    let result = null;
    if (anns != null && anns.length > 0) {
      let begin = anns[0].begin_ts;
      let end = anns[0].end_ts;
      const labels = [];
      let notes = '';
      let id = 0;
      for (const a of anns) {
        begin = a.begin_ts.isAfter(begin) ? a.begin_ts : begin;
        end = a.end_ts.isBefore(end) ? a.end_ts : end;
        labels.push(...a.labels);
        notes += a.notes;
        id += a.id;
      }
      result = Info.newAnnotation(id, begin, end, notes, labels);
    }
    return result;
  }

  static newAnnotation(id, begin, end, notes, labelsArr) {
    return {
      id: id,
      begin_ts: begin,
      end_ts: end,
      labels: labelsArr,
      notes: notes
    };
  }

  static smallestGreaterThanZero(array, predicate) {
    let result;
    let minValue = null;
    for (const element of array) {
      const value = predicate(element);
      if (value > 0) {
        if (minValue == null || value < minValue) {
          minValue = value;
          result = element;
        }
      }
    }
    return result;
  }
}
